import { startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useLogout } from "@core/features/auth";
import { useIsLoginDisabled } from "@platform/hooks/useIsLoginDisabled";
import { usePlatformStore } from "@platform/store";
import { Type } from "@themes/pollinator/ui/Type";

export function LogoutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = usePlatformStore((s) => s.clearAuth);
  const { isLoginDisabled } = useIsLoginDisabled();

  // Ending the session in this browser is what signing out means to the person
  // doing it, so it happens whether or not the request to the server succeeded.
  // The error path used to navigate away and leave the stored session intact,
  // which matters most while login is closed: the app sends anybody still
  // signed in to this page, so a failed request left them still carrying a
  // token, and looking signed in, on a site that had just tried to sign them
  // out.
  //
  // Signing out ordinarily ends on the login page. While login is closed that
  // page does nothing but redirect home, and arriving there shows the auth
  // layout and its spinner on the way, which reads as a sign-in screen to
  // somebody who was just signed out. The destination is already known here,
  // so go straight to it.
  const endSession = () => {
    startTransition(() => {
      queryClient.clear();
      clearAuth();
      navigate(isLoginDisabled ? "/" : "/login");
    });
  };

  useLogout({
    onSuccess: endSession,
    onError: endSession,
    autoLogout: true,
  });

  return (
    <div className="flex-1 flex flex-col justify-between bg-brand-50 text-brand-800">
      <div className="flex flex-1 flex-col items-center justify-center">
        <Type as="h1" variant="h3">
          Logging out...
        </Type>
      </div>
    </div>
  );
}

export default LogoutPage;


